import React, { useRef } from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import YouTubeIcon from "@mui/icons-material/YouTube";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import LayersClearIcon from "@mui/icons-material/LayersClear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import Divider from "@mui/material/Divider";

import MoreVertIcon from "@mui/icons-material/MoreVert";

import { DeleteDialog } from "./TreeView/Components/DeleteDialog";

const getFioPreviewAssets = (fioAssetIds: any) => {
	return Object.entries(fioAssetIds || {}).map(([id, fioAsset]: any) => {
		const fioAssetId = fioAsset?.fio_asset_id || id;

		return {
			...(fioAsset || {}),
			provider: "fio",
			id: fioAssetId,
			selection_id: fioAssetId,
			preview_asset_id: fioAssetId,
			fio_asset_id: fioAssetId,
			file_name: fioAsset?.file_name || fioAsset?.name || fioAssetId,
		};
	});
};

const getCoconutPreviewAssets = (coconutJobs: any) => {
	return Object.entries(coconutJobs || {}).map(([uploadId, job]: any) => {
		return {
			...(job || {}),
			provider: "coconut",
			id: uploadId,
			selection_id: uploadId,
			preview_asset_id: uploadId,
			upload_id: uploadId,
			uploadId,
			coconut_upload_id: uploadId,

			// Backwards-compatible alias for older preview/menu code.
			// This is not a real Frame.io id for Coconut.
			fio_asset_id: uploadId,

			file_name: job?.fileName || job?.file_name || job?.sourceFileName || job?.source_file_name || job?.announcedFile?.fileName || uploadId,
		};
	});
};

const getMenuPreviewAssets = ({ preview_assets, fio_asset_ids, coconut_jobs }: any) => {
	if (Array.isArray(preview_assets) && preview_assets.length) {
		return preview_assets;
	}

	return [...getFioPreviewAssets(fio_asset_ids), ...getCoconutPreviewAssets(coconut_jobs)];
};

const getPreviewAssetFileName = (previewAsset: any) => {
	return (
		previewAsset?.file_name ||
		previewAsset?.fileName ||
		previewAsset?.sourceFileName ||
		previewAsset?.source_file_name ||
		previewAsset?.name ||
		previewAsset?.preview_asset_id ||
		previewAsset?.fio_asset_id ||
		previewAsset?.upload_id ||
		previewAsset?.uploadId ||
		previewAsset?.id ||
		""
	);
};

const getUniqueFileNames = (previewAssets: any[]) => {
	return previewAssets
		.map((previewAsset: any) => getPreviewAssetFileName(previewAsset))
		.filter(Boolean)
		.filter((fileName: string, index: number, arr: string[]) => arr.indexOf(fileName) === index);
};

export default function VersionMenu({ single_link, all_link, single_password, all_password, version, version_name, version_id, job_ident, version_number_string, fio_asset_ids, coconut_jobs, preview_assets, sx }: any) {
	const deleteDialogRef = useRef<any>();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const [showTooltip, setShowTooltip] = React.useState<boolean>(false);

	const menuPreviewAssets = getMenuPreviewAssets({
		preview_assets,
		fio_asset_ids,
		coconut_jobs,
	});

	const hasSingleLink = typeof single_link !== "undefined" && single_link !== "";
	const hasAllLink = typeof all_link !== "undefined" && all_link !== "";
	const canDeleteVersion = typeof version_id !== "undefined" && version_id !== "";

	const hasAnyAction = hasSingleLink || hasAllLink || canDeleteVersion;

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const openInNewTab = (url: string, password?: string) => {
		const openLink = () => {
			window.open(url, "_blank", "noopener,noreferrer");
			handleClose();
		};

		if (!password) {
			openLink();
			return;
		}

		navigator.clipboard.writeText(password).then(
			() => {
				openLink();
			},
			() => {
				openLink();
			}
		);
	};

	const writeClipboardTextFallback = (text: string) => {
		return navigator.clipboard.writeText(text);
	};

	const onCopy = (event: any) => {
		event.stopPropagation();
		event.preventDefault();

		if (!(single_password && single_link)) {
			return;
		}

		const allFileNames = getUniqueFileNames(menuPreviewAssets);
		const fileNamesText = allFileNames.join(", ");

		const originalText = `Reviewlink '${version_name}'\n\nFiles: ${fileNamesText}\n\nLink: ${single_link}\nPassword: ${single_password}`;
		const htmlText = `<h3 style="display:inline;">Reviewlink '${version_name}'</h3></br><b>Files:</b> ${fileNamesText}</br></br><b>Link:</b> ${single_link}</br><b>Password:</b> ${single_password}`;

		if (typeof ClipboardItem === "undefined") {
			writeClipboardTextFallback(originalText).then(
				() => {
					setShowTooltip(true);
				},
				() => {}
			);

			return;
		}

		const blobHtml = new Blob([htmlText], { type: "text/html" });
		const blobText = new Blob([originalText], { type: "text/plain" });
		const data = [
			new ClipboardItem({
				["text/plain"]: blobText,
				["text/html"]: blobHtml,
			}),
		];

		navigator.clipboard.write(data).then(
			() => {
				setShowTooltip(true);
			},
			() => {
				writeClipboardTextFallback(originalText).then(
					() => {
						setShowTooltip(true);
					},
					() => {}
				);
			}
		);
	};

	const handleOnTooltipClose = () => {
		setShowTooltip(false);
	};

	const handleDeleteClick = (event: any) => {
		event.stopPropagation();
		event.preventDefault();

		deleteDialogRef.current?.openDialog?.();
	};

	if (!hasAnyAction) {
		return null;
	}

	return (
		<Box sx={{ ...sx, typography: "body2" }}>
			<DeleteDialog ref={deleteDialogRef} onCloseCallback={handleClose} itemToDelete={{ job_ident: job_ident, item_name: version_name, item_id: version_id, item_type: "version" }} />

			<IconButton size="small" disableFocusRipple={false} id="basic-button" aria-controls={open ? "basic-menu" : undefined} aria-haspopup="true" aria-expanded={open ? "true" : undefined} onClick={handleClick}>
				<MoreVertIcon />
			</IconButton>

			<Menu
				id="basic-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				MenuListProps={{
					"aria-labelledby": "basic-button",
				}}
			>
				{hasSingleLink ? (
					<Tooltip title={`Open "${version_name}" and copy password`} placement="top" arrow>
						<MenuItem sx={{ mt: 0 }} dense onClick={() => openInNewTab(single_link, single_password)}>
							<ListItemIcon>
								<YouTubeIcon />
							</ListItemIcon>

							<ListItemText>Version {version_number_string}</ListItemText>

							<Tooltip open={showTooltip} title={"Copied to clipboard!"} leaveDelay={1500} onClose={handleOnTooltipClose}>
								<IconButton sx={{ ml: 1 }} onClick={onCopy}>
									<ContentCopyIcon sx={{ height: "12px", width: "12px" }} fontSize="inherit" />
								</IconButton>
							</Tooltip>
						</MenuItem>
					</Tooltip>
				) : null}

				{hasAllLink ? (
					<MenuItem sx={{ mt: 0 }} dense onClick={() => openInNewTab(all_link, all_password || single_password)}>
						<ListItemIcon>
							<SubscriptionsIcon />
						</ListItemIcon>
						<ListItemText>All Versions</ListItemText>
					</MenuItem>
				) : null}

				{hasSingleLink || hasAllLink ? (
					<MenuItem disabled>
						<Divider sx={{ p: 0, height: "2px", width: "100%", background: "rgba(255,255,255,0.25)", zIndex: 1 }} />
					</MenuItem>
				) : null}

				{canDeleteVersion ? (
					<MenuItem sx={{ color: "error.main", mt: 0, opacity: 0.5 }} dense onClick={handleDeleteClick}>
						<ListItemIcon sx={{ color: "error.main" }}>
							<LayersClearIcon />
						</ListItemIcon>
						<ListItemText>Mark Deleted</ListItemText>
					</MenuItem>
				) : null}
			</Menu>
		</Box>
	);
}
